/**
 * Persisted selection for how Enter behaves in writing inputs.
 */
export type WritingEnterKeyMode = 'auto' | 'send' | 'newline';

/**
 * Resolved Enter behavior used by a concrete text input.
 */
export type ResolvedEnterBehavior = 'send' | 'newline';

/**
 * Local storage key for the frontend writing Enter preference.
 */
export const WRITING_ENTER_KEY_MODE_STORAGE_KEY = 'lp.ui.writing.enterKeyMode';
export const WRITING_ENTER_KEY_MODE_CHANGE_EVENT =
  'lp.ui.writing.enterKeyModeChange';

const VALID_WRITING_ENTER_KEY_MODES: readonly WritingEnterKeyMode[] = [
  'auto',
  'send',
  'newline',
];

/**
 * Returns true when the provided value matches a supported Enter preference.
 *
 * @param value - Unknown persisted value to validate.
 * @returns Whether the value is a supported writing Enter mode.
 */
export const isWritingEnterKeyMode = (
  value: unknown
): value is WritingEnterKeyMode =>
  typeof value === 'string' &&
  VALID_WRITING_ENTER_KEY_MODES.includes(value as WritingEnterKeyMode);

/**
 * Reads the persisted writing Enter preference from local storage.
 *
 * @returns The stored mode, or `auto` when missing/invalid/unavailable.
 */
export const readWritingEnterKeyMode = (): WritingEnterKeyMode => {
  if (typeof window === 'undefined') {
    return 'auto';
  }

  const storedValue = window.localStorage.getItem(
    WRITING_ENTER_KEY_MODE_STORAGE_KEY
  );

  return isWritingEnterKeyMode(storedValue) ? storedValue : 'auto';
};

/**
 * Persists the writing Enter preference in local storage.
 *
 * @param mode - Mode selected by the user.
 */
export const persistWritingEnterKeyMode = (mode: WritingEnterKeyMode): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(WRITING_ENTER_KEY_MODE_STORAGE_KEY, mode);
  window.dispatchEvent(new Event(WRITING_ENTER_KEY_MODE_CHANGE_EVENT));
};

/**
 * Resolves the runtime Enter behavior from browser input capabilities.
 *
 * Resolution order:
 * 1. `send` when `window` is unavailable
 * 2. `send` when the primary pointer is fine
 * 3. `newline` when coarse/touch-first capabilities are detected
 * 4. `send` otherwise
 *
 * @returns Runtime Enter behavior for the current environment.
 */
export const resolveAutoEnterBehavior = (): ResolvedEnterBehavior => {
  if (typeof window === 'undefined') {
    return 'send';
  }

  if (window.matchMedia('(pointer: fine)').matches) {
    return 'send';
  }

  const hasCoarsePointer =
    window.matchMedia('(pointer: coarse)').matches ||
    window.matchMedia('(any-pointer: coarse)').matches;
  const hasTouchPoints = navigator.maxTouchPoints > 0;

  if (hasCoarsePointer || hasTouchPoints) {
    return 'newline';
  }

  return 'send';
};

/**
 * Resolves the effective Enter behavior for a selected mode.
 *
 * @param mode - Selected stored mode.
 * @returns Concrete Enter behavior for the input.
 */
export const resolveEnterBehavior = (
  mode: WritingEnterKeyMode
): ResolvedEnterBehavior => {
  if (mode === 'auto') {
    return resolveAutoEnterBehavior();
  }

  return mode;
};
